import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Heart, Star, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product } from '@/types/product';
import { useCart } from '@/contexts/CartContext';

type ProductVariant = Product['variants'][0];

interface ProductQuickViewProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart?: (product: Product, variant: ProductVariant, quantity: number) => void;
}

export const ProductQuickView: React.FC<ProductQuickViewProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart
}) => {
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const { addToCart: addToCartContext } = useCart();

  if (!product) return null;

  // Get all available images for gallery
  const productImages = [product.images.main, ...product.images.gallery].filter(Boolean);
  const hasMultipleImages = productImages.length > 1;

  const handleAddToCart = async () => {
    if (!selectedSize || (product.variants.some(v => v.color) && !selectedColor)) {
      return;
    }

    setIsLoading(true);
    
    try {
      const matchingVariant = product.variants.find(v => 
        v.size === selectedSize && (!v.color || v.color === selectedColor)
      ) || product.variants[0];
      
      // Add to cart using context
      await addToCartContext(product, matchingVariant, quantity);
      
      // Call the legacy onAddToCart prop if provided (for backwards compatibility)
      if (onAddToCart) {
        onAddToCart(product, matchingVariant, quantity);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  const sizeOptions = [...new Set(product.variants.map(v => v.size).filter(Boolean))];
  const colorOptions = [...new Set(product.variants.map(v => v.color).filter(Boolean))];
  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountPercentage = hasDiscount ? 
    Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100) : 0;

  const currentImage = productImages[currentImageIndex];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden grid md:grid-cols-2 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md"
              onClick={onClose}
              aria-label="Close quick view"
            >
              <X className="w-4 h-4" />
            </Button>

            {/* Image Gallery Section */}
            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 min-h-[400px] md:min-h-full">
              {/* Product Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                {product.badge && (
                  <Badge
                    variant={product.badge === 'NEW' ? 'default' : 'secondary'}
                    className={`text-xs font-bold shadow-lg ${
                      product.badge === 'NEW' 
                        ? 'bg-lime-400 text-black hover:bg-lime-500' 
                        : product.badge === 'BESTSELLER'
                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                        : product.badge === 'PREMIUM'
                        ? 'bg-purple-500 text-white hover:bg-purple-600'
                        : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >
                    {product.badge}
                  </Badge>
                )}
                {hasDiscount && (
                  <Badge variant="destructive" className="text-xs font-bold shadow-lg bg-red-500 text-white hover:bg-red-600">
                    -{discountPercentage}%
                  </Badge>
                )}
              </div>

              {/* Main Image */}
              <img
                src={currentImage}
                alt={`${product.name} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (!img.src.includes('placeholder')) {
                    img.src = `https://via.placeholder.com/600x600/10b981/ffffff?text=${encodeURIComponent(product.name.replace(/\s+/g, '+'))}`;
                  }
                }}
              />

              {/* Image Navigation */}
              {hasMultipleImages && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>

                  {/* Image Dots */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {productImages.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${
                          index === currentImageIndex 
                            ? 'bg-lime-400 w-6' 
                            : 'bg-white/60 hover:bg-white/80'
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Favorite Button */}
              <Button
                size="sm"
                variant="secondary"
                className={`absolute top-4 right-12 w-10 h-10 rounded-full shadow-md transition-colors duration-200 ${
                  isFavorited 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-white/90 text-gray-700 hover:bg-red-500 hover:text-white'
                }`}
                onClick={() => setIsFavorited(!isFavorited)}
                aria-label={isFavorited ? `Remove ${product.name} from favorites` : `Add ${product.name} to favorites`}
              >
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              </Button>
            </div>

            {/* Product Details Section */}
            <div className="p-6 overflow-y-auto">
              {/* Rating */}
              {product.rating && (
                <div className="flex items-center gap-2 mb-3" role="img" aria-label={`Rating: ${product.rating} out of 5 stars`}>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating!)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">
                    ({product.reviewCount || 0} reviews)
                  </span>
                </div>
              )}

              {/* Product Name & Category */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {product.name}
              </h2>
              <p className="text-sm text-gray-500 mb-4 uppercase tracking-wide">
                {product.category}
              </p>

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl font-bold text-gray-900">
                  ${product.price}
                </span>
                {hasDiscount && (
                  <span className="text-lg text-gray-500 line-through">
                    ${product.originalPrice}
                  </span>
                )}
              </div>

              {/* Product Description */}
              {product.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Variant Selection */}
              <div className="space-y-4 mb-6">
                {/* Size Selector */}
                {sizeOptions.length > 0 && (
                  <div>
                    <label htmlFor="quick-view-size" className="text-sm font-semibold text-gray-900 mb-2 block">
                      Size
                    </label>
                    <Select value={selectedSize} onValueChange={setSelectedSize}>
                      <SelectTrigger id="quick-view-size" className="w-full bg-white border-gray-300 text-gray-900 hover:border-lime-400 focus:border-lime-400 focus:ring-lime-400">
                        <SelectValue placeholder="Select size" className="text-gray-500" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {sizeOptions.map((size) => (
                          <SelectItem key={size} value={size} className="text-gray-900 hover:bg-lime-50 hover:text-lime-900">
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Color Selector */}
                {colorOptions.length > 0 && (
                  <div>
                    <label htmlFor="quick-view-color" className="text-sm font-semibold text-gray-900 mb-2 block">
                      Color
                    </label>
                    <Select value={selectedColor} onValueChange={setSelectedColor}>
                      <SelectTrigger id="quick-view-color" className="w-full bg-white border-gray-300 text-gray-900 hover:border-lime-400 focus:border-lime-400 focus:ring-lime-400">
                        <SelectValue placeholder="Select color" className="text-gray-500" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {colorOptions.map((color) => (
                          <SelectItem key={color} value={color} className="text-gray-900 hover:bg-lime-50 hover:text-lime-900">
                            {color}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Quantity Selector */}
                <div>
                  <label className="text-sm font-semibold text-gray-900 mb-2 block">
                    Quantity
                  </label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      className="w-10 h-10 border-gray-300 text-gray-700 hover:border-lime-400 hover:text-lime-700 disabled:text-gray-400"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-semibold min-w-[3ch] text-center text-gray-900">
                      {quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={incrementQuantity}
                      className="w-10 h-10 border-gray-300 text-gray-700 hover:border-lime-400 hover:text-lime-700"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                disabled={isLoading || (!selectedSize || (colorOptions.length > 0 && !selectedColor))}
                className="w-full bg-lime-400 hover:bg-lime-500 text-black font-semibold h-12 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`Add ${quantity} ${product.name} to cart`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Adding to Cart...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Add {quantity > 1 ? `${quantity} ` : ''}to Cart
                  </div>
                )}
              </Button>

              {/* Selection Hint */}
              {(!selectedSize || (colorOptions.length > 0 && !selectedColor)) && (
                <p className="text-sm text-red-600 mt-3 text-center font-medium bg-red-50 py-2 px-4 rounded-lg">
                  {!selectedSize ? 'Please select a size' : 'Please select a color'}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductQuickView; 